import ApolloClient from 'apollo-client';
import { RequestState } from '../../auth/hooks/useRequestState';
import { reportBuilderQuery } from '../../apollo';
import { useParams } from 'react-router-dom';
import { Report } from '../../components/WelcomeBack/utils';
import JSON5 from 'json5';
import { capitalizeOnlyFirstLetter } from '../../utils/stringUtils';
import { AllocationFieldsFragment } from '../../apollo/fragments/allocation';
import { IndicatorDetails } from 'npo/pages/ReportBuilder/Components/IndicatorDetails/utils';

export interface ReportBuilderData extends Report, AllocationFieldsFragment {
  primaryInterventionType: string | null;
  primaryInterventionSubtypes: string | null;
  interventionDosageLevel: string | null;
  interventionDosageNumber: string | null;
  interventionDosageType: string | null;
  successStory: string | null;
  lessonLearned: string | null;
  storyFileId: string | null;
  primaryInterventionDescription: string | null; // legacy
  customQuestionResponses: string | null; // legacy (JSON), useCustomQuestions instead
}

export const getReportIdByPathname = (
  pathname: string,
): string | number | undefined => {
  if (
    pathname.startsWith('/npo/report-builder') ||
    pathname.includes('/feedback/') ||
    pathname.startsWith('/report/')
  ) {
    const id = pathname.split('/').pop();
    if (id) return Number(id) || undefined;
  }

  return undefined;
};

export const getReportId = (pathname: string): string | number | undefined => {
  const params = useParams<{ feedbackId: string; reportId: string }>();
  const id = params.feedbackId || params.reportId;
  if (id) return id;

  return getReportIdByPathname(pathname);
};

export const unableToLoadReportErrorMessage = 'Unable to load this report.';
export const getReportState = (
  state: RequestState<{ allReports: ReportBuilderData[] }>,
): RequestState<ReportBuilderData> => {
  if (state.type === 'REQUEST_SUCCESS') {
    const report = state.data?.allReports?.[0];
    return report
      ? {
          type: 'REQUEST_SUCCESS',
          data: report,
        }
      : {
          data: null,
          type: 'REQUEST_ERROR',
          error: {
            isAxiosError: false,
            message: unableToLoadReportErrorMessage,
            name: unableToLoadReportErrorMessage,
            response: {
              status: 400,
            },
            stack: unableToLoadReportErrorMessage,
          },
        };
  }

  return state;
};

export const getReport = (
  client: ApolloClient<object>,
  id: number | string,
) => {
  return client.query({
    variables: { id },
    query: reportBuilderQuery,
    fetchPolicy: 'network-only',
  });
};

interface PrimaryType {
  values: {
    label: string;
    name: string;
    description: string;
    icon: string;
  };
}

interface SecondaryType {
  values: {
    value: string;
    description: string;
    selected?: boolean;
  }[];
}

const parseInterventionField = <T,>(value: string | null | undefined): T | string | undefined => {
  if (!value) {
    return undefined;
  }
  const trimmed = value.trim();
  if (!trimmed || trimmed === '{}' || trimmed === '[]') {
    return undefined;
  }
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return trimmed;
  }
  try {
    return JSON5.parse(trimmed) as T;
  } catch {
    return trimmed;
  }
};

export interface ActivitiesFromReport {
  serviceDelivery: string;
  serviceSubType: string;
  serviceSecondarySubType: string;
  averageValueOfGoods: string;
  frequencyOfGoods: string;
  durationOfGoods: string;
  primaryInterventionDescription: string;
}

export const activitiesFromReport = (
  report: ReportBuilderData | null | undefined,
): ActivitiesFromReport => {
  if (!report) {
    return {
      serviceDelivery: '',
      serviceSubType: '',
      serviceSecondarySubType: '',
      averageValueOfGoods: '',
      frequencyOfGoods: '',
      durationOfGoods: '',
      primaryInterventionDescription: '',
    };
  }

  const {
    primaryInterventionType,
    primaryInterventionSubtypes,
    interventionDosageLevel,
    interventionDosageNumber,
    interventionDosageType,
  } = report;

  const primaryType = parseInterventionField<PrimaryType>(primaryInterventionType);
  const subType = parseInterventionField<SecondaryType>(primaryInterventionSubtypes);

  return {
    serviceDelivery:
      capitalizeOnlyFirstLetter(
        typeof primaryType === 'string' ? primaryType : primaryType?.values?.name,
      ) || '',
    serviceSubType:
      (typeof subType === 'string' ? subType : subType?.values?.[0]?.description) || '',
    serviceSecondarySubType:
      (typeof subType === 'string' ? '' : subType?.values?.[1]?.description) || '',
    averageValueOfGoods: interventionDosageLevel || '',
    frequencyOfGoods: interventionDosageNumber || '',
    durationOfGoods: interventionDosageType || '',
    primaryInterventionDescription: report.primaryInterventionDescription || '',
  };
};

export interface ForecastFieldProps {
  hide: boolean;
  disabled: boolean;
  label: string;
  placeholder: string;
}

export const getHideForecastField = (
  report: ReportBuilderData | null | undefined,
  indicator: IndicatorDetails,
  isReportPassed: boolean,
): ForecastFieldProps => {
  const label = 'Forecast';
  const placeholder = 'Forecast';
  if (!report) {
    return { hide: false, disabled: false, label, placeholder };
  }
  if (!isReportPassed) {
    return { hide: false, disabled: false, label, placeholder };
  }
  if (indicator?.forecast) {
    return { hide: false, disabled: true, label, placeholder };
  }
  return {
    hide: true,
    disabled: false,
    label,
    placeholder,
  };
};

export const getHideFinalForecastField = (
  report: ReportBuilderData | null | undefined,
  indicator: IndicatorDetails,
  isReportPassed: boolean,
): ForecastFieldProps => {
  const label = 'Final Results';
  const placeholder = 'Final Results';
  // The final results field only becomes active once the report has passed
  // (stage INTERIM/FINAL, or the end date is in the past).
  if (!report || !isReportPassed) {
    return { hide: true, disabled: false, label, placeholder };
  }
  if (report.stage === 'INTERIM') {
    return {
      hide: false,
      disabled: false,
      label: 'Updated Forecast',
      placeholder: 'Updated',
    };
  }
  return {
    hide: false,
    disabled: false,
    label,
    placeholder,
  };
};
